import {
  AUTHORING_PATH_FIELD_MODE_DEFINITIONS,
  DEFAULT_REPORT_CACHE_FILE_NAME,
  DEFAULT_SYSTEM_REPORT_CACHE_PATH,
  DEFAULT_USER_REPORT_CACHE_PATH,
  USER_DATA_PATH_ROOT,
  authoringPathBasename,
  buildReportCachePathPair,
  classifyAuthoringPath,
  composeAuthoringPath,
  deriveSystemReportCachePath,
  deriveUserReportCachePath,
  isHttpPathUrl,
  resolveUserPath,
  switchAuthoringPathScope,
  toAbsoluteSitePath,
  type AuthoringPathFieldMode,
} from "./pathContract";
import type {
  EditorFileBrowserScope,
} from "./editorFileBrowser";

export const EDITOR_PATH_INPUT_SCOPES = [
  "site",
  "user",
  "system",
  "external",
] as const;

export type EditorPathInputScope =
  (typeof EDITOR_PATH_INPUT_SCOPES)[number];

export interface EditorPathInputScopeSwitchResult {
  value: string;
  changed: boolean;
  scope: EditorPathInputScope;
  reason?: "unknown-user-path" | "unsafe-relative-segment";
}

export interface EditorReportCachePaths {
  user: string;
  system: string;
}

export function normalizeEditorPathInputScopes(
  scopes: readonly EditorPathInputScope[] | null | undefined,
  options: {
    mode: AuthoringPathFieldMode;
    allowHttpUrls?: boolean;
  },
): EditorPathInputScope[] {
  const defaults: EditorPathInputScope[] = options.mode === "report-cache"
    ? ["user", "system"]
    : ["site"];
  const requested = scopes?.length ? scopes : defaults;

  const normalized = [...new Set(requested)].filter((scope) =>
    EDITOR_PATH_INPUT_SCOPES.includes(scope)
    && (scope !== "external" || options.allowHttpUrls),
  );

  return normalized.length ? normalized : defaults;
}

export function inferEditorPathInputScope(
  value: string | null | undefined,
  scopes: readonly EditorPathInputScope[],
  fallback?: EditorPathInputScope,
): EditorPathInputScope {
  const classification = classifyAuthoringPath(value, {
    allowHttpUrls: true,
  });
  if (
    classification.scope !== "unknown"
    && scopes.includes(classification.scope)
  ) {
    return classification.scope;
  }
  if (fallback && scopes.includes(fallback)) return fallback;
  return scopes[0] ?? "site";
}

export function switchEditorPathInputScope(
  value: string | null | undefined,
  targetScope: EditorPathInputScope,
): EditorPathInputScopeSwitchResult {
  const normalized = (value ?? "").trim();

  if (targetScope === "external") {
    const externalValue = isHttpPathUrl(normalized) ? normalized : "https://";
    return {
      value: externalValue,
      changed: externalValue !== normalized,
      scope: targetScope,
    };
  }

  if (isHttpPathUrl(normalized)) {
    const reset = switchAuthoringPathScope("", targetScope);
    return {
      value: reset.value,
      changed: reset.value !== normalized,
      scope: targetScope,
    };
  }

  const switched = switchAuthoringPathScope(normalized, targetScope);
  return {
    value: switched.value,
    changed: switched.changed,
    scope: switched.reason ? inferEditorPathInputScope(
      normalized,
      ["site", "user", "system"],
      "site",
    ) : targetScope,
    reason: switched.reason === "unknown-user-path"
      || switched.reason === "unsafe-relative-segment"
      ? switched.reason
      : undefined,
  };
}

export function applyEditorFileBrowserSelection(
  currentValue: string | null | undefined,
  selectedPath: string,
  mode: AuthoringPathFieldMode,
): string {
  const definition = AUTHORING_PATH_FIELD_MODE_DEFINITIONS[mode];
  if (!definition.preserveFilename) return selectedPath;

  return composeAuthoringPath(
    selectedPath,
    authoringPathBasename(currentValue)
      || (mode === "report-cache" ? DEFAULT_REPORT_CACHE_FILE_NAME : ""),
  );
}

export function resolveEditorPathInputPreview(
  value: string | null | undefined,
  userId: string | null | undefined,
): string | null {
  const normalized = (value ?? "").trim();
  if (!normalized.includes("<userId>") || !userId?.trim()) return null;

  try {
    return resolveUserPath(normalized, userId);
  } catch {
    return null;
  }
}

export function editorPathInputCandidateContextMessage(
  scope: EditorPathInputScope,
  mode: AuthoringPathFieldMode,
  userId: string | null | undefined,
): string | null {
  if (scope !== "user") return null;

  try {
    resolveUserPath(USER_DATA_PATH_ROOT, userId);
    return null;
  } catch {
    const canUseVirtualFolder =
      AUTHORING_PATH_FIELD_MODE_DEFINITIONS[mode].preserveFilename;
    return canUseVirtualFolder
      ? "No preview candidate is selected. The tokenized output folder remains available, but existing candidate files cannot be browsed."
      : "Select a preview candidate before browsing current-candidate files or folders.";
  }
}

export function deriveEditorReportCachePaths(
  value: string | null | undefined,
): EditorReportCachePaths | null {
  const classification = classifyAuthoringPath(value, {
    allowHttpUrls: true,
  });

  if (classification.kind === "empty") {
    return {
      user: DEFAULT_USER_REPORT_CACHE_PATH,
      system: DEFAULT_SYSTEM_REPORT_CACHE_PATH,
    };
  }
  if (
    classification.scope === "external"
    || classification.scope === "unknown"
  ) {
    return null;
  }

  if (classification.scope === "user") {
    const system = deriveSystemReportCachePath(classification.value);
    return system
      ? { user: toAbsoluteSitePath(classification.value), system }
      : null;
  }

  if (classification.scope === "system") {
    const user = deriveUserReportCachePath(classification.value);
    return user
      ? { user, system: toAbsoluteSitePath(classification.value) }
      : null;
  }

  const user = switchAuthoringPathScope(classification.value, "user");
  const system = switchAuthoringPathScope(classification.value, "system");
  if (user.reason || system.reason) return null;

  if (!authoringPathBasename(classification.value)) {
    return buildReportCachePathPair();
  }
  return {
    user: user.value,
    system: system.value,
  };
}

export function browserScopesForEditorPathInput(
  scopes: readonly EditorPathInputScope[],
): EditorFileBrowserScope[] {
  return scopes.filter(
    (scope): scope is EditorFileBrowserScope => scope !== "external",
  );
}
